import { Position, Radio, RadioGroup } from "@blueprintjs/core"
import { TimePrecision } from "@blueprintjs/datetime"
import CustomButton from "components/Atoms/CustomButton"
import CustomCheckbox from "components/Atoms/CustomCheckbox"
import CustomDatePicker from "components/Atoms/CustomDatePicker"
import CustomFormGroup from "components/Atoms/CustomFormGroup"
import CustomTextArea from "components/Atoms/CustomTextArea"
import Divider from "components/Atoms/Divider"
import RichTextEditor from "components/Atoms/RichTextEditor"
import StaticSelector from "components/Atoms/StaticSelector"
import StringInput from "components/Atoms/StringInput"
import DynamicSelector from "components/Molecules/DynamicSelector"
import FileUpload from "components/Molecules/FileUpload"
import FormController from "components/Molecules/FormController"
import SubHeader from "components/Organisms/SubHeader"
import SearchSelector from "components/Templates/SearchSelector"
import { NOTIFICATION_KINDS } from "data/static.items"
import { useNotificationTriggerLazyQuery } from "generated/graphql"
import {
  ALL_NOTIFICATION_PROVIDERS,
  ALL_NOTIFICATION_TEMPLATES_V1,
  ALL_USER_QUERIES,
} from "graphql/notification/notification.queries.gql"

import { SEARCH_SELLABLE_ITEMS } from "graphql/selector.queries.gql"
import { isEmpty, pick } from "lodash-es"
import TemplateFilter from "pages/reports/TemplateFilter"
import React, { useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { LINK_TYPES } from "./add-template"
import Variables from "./add-template/Variables"
import useNotificationTriggerStore from "./notification-trigger.store"
import TriggerWarning from "./trigger-warning"
import CustomCard from "components/Atoms/CustomCard"

const CreateNotificationTrigger = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const {
    data,
    setData,
    resetData,
    addListItem,
    removeListItem,
    initializeState,
  } = useNotificationTriggerStore(store =>
    pick(store, [
      "data",
      "setData",
      "resetData",
      "addListItem",
      "removeListItem",
      "initializeState",
    ])
  )

  const kind = data.kind?.id
  const allFilters = data.userQuery?.filters
  const filtersState = data.filters
  const dataSources = data.template?.dataSource?.inputs
  const pushNotificationLength = data?.content?.extraFields?.image ? 50 : 434
  const isPushNot = data?.kind?.id === "push"
  const [getDetails] = useNotificationTriggerLazyQuery({
    onCompleted({ notificationTriggerDetail: data }) {
      if (!parseInt(id) || !data) return

      initializeState({
        id: data.id,
        broadcastTime: new Date(data.broadcastTime),
        content: {
          ...data.config.content,
          extraFields: {
            image: data.config.content.image,
            data: JSON.parse(data.config.content.data),
          },
        },
        dataVariables: data?.dataVariables,
        providerVariables: isEmpty(data.providerVariables)
          ? []
          : Object.keys(data.providerVariables).map(key => ({
              key: key,
              value: data.providerVariables[key],
            })),
        kind: NOTIFICATION_KINDS.find(
          kind => kind.id === data.provider.kind.toLowerCase()
        ),
        filters: data.config.filters?.map(filter => ({
          field: {
            ...data.userQuery.filters.find(f => f.id === filter.id),
            value: filter.value,
          },
          exclude: filter.exclude ? 1 : 0,
        })),
        provider: data.provider,
        isProgrammatic: data.template.isProgrammatic,
        template: data.template,
        userQuery: data.userQuery,
      })
    },
  })
  useEffect(() => {
    if (parseInt(id)) {
      getDetails({ variables: { id } })
    }
    return function cleanup() {
      resetData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const disabled = parseInt(id) ? true : false
  return (
    <SubHeader
      header={
        <div>
          <CustomButton
            disabled={disabled}
            onClick={resetData}
            className="mr-2"
          >
            Clear
          </CustomButton>
          <TriggerWarning disabled={disabled} />
        </div>
      }
      breadrumbs={[
        {
          href: "/d/registration/notifications",
          text: "Notification Triggers",
          onClick(e) {
            e.preventDefault()
            navigate("/d/registration/notifications")
          },
        },
      ]}
    >
      <FormController
        disabled={disabled}
        disableAll
        source={data}
        onChange={setData}
      >
        <CustomCard className="shadow-sm rounded-md p-3 vt-form-grid">
          <div className="grid grid-cols-4 gap-4">
            <StaticSelector
              path="kind"
              label="Sent through"
              items={NOTIFICATION_KINDS}
            />
            <DynamicSelector
              path="provider"
              label="Provider"
              query={ALL_NOTIFICATION_PROVIDERS}
              variables={{
                filter: {
                  kind_In: kind,
                },
              }}
            />
            <DynamicSelector
              label="Template"
              path="template"
              query={ALL_NOTIFICATION_TEMPLATES_V1}
            />
            <div className="flex items-end">
              <CustomFormGroup label="Broadcast Time">
                <CustomDatePicker
                  valueAs="object"
                  format="MMMM Do YYYY, h:mm:ss A"
                  path="broadcastTime"
                  minDate={new Date()}
                  popoverProps={{
                    position: Position.LEFT,
                    disabled,
                  }}
                  buttonProps={{
                    disabled,
                  }}
                  closeOnSelect={false}
                  timePickerProps={{
                    useAmPm: true,
                    showArrowButtons: true,
                    precision: TimePrecision.SECOND,
                  }}
                />
              </CustomFormGroup>
              <CustomCheckbox
                className="ml-3"
                path="isProgrammatic"
                label="Programmatic Access"
              />
            </div>
          </div>
          <Divider />
          <div>
            <h6 className="mb-2">Provider Related Information</h6>

            <Variables
              disabled={disabled}
              list={data.providerVariables}
              onAdd={value => addListItem("providerVariables", value)}
              onRemove={idx => removeListItem("providerVariables", idx)}
              className={"grid grid-cols-2 gap-3"}
            />
          </div>
          <Divider />
          {dataSources?.length && data.isProgrammatic && (
            <>
              <div>
                <h6 className="mb-2">Data Variables</h6>
                <div className="grid grid-cols-4 gap-4">
                  {dataSources.map(input => {
                    return (
                      <>
                        <StringInput
                          fill={false}
                          placeholder="Enter value for the variable"
                          label={
                            <span>
                              Variable -{" "}
                              <span className="font-bold">{input}</span>
                            </span>
                          }
                          big={false}
                          path={`dataVariables.${input}`}
                        />
                      </>
                    )
                  }) || "No inputs!"}
                </div>
              </div>
              <Divider />
            </>
          )}

          <StringInput
            disabled={kind === "sms"}
            path="content.title"
            big={false}
            className="col-span-full mt-[16px]"
            label="Message Subject (Optional)"
          />

          <CustomFormGroup label="Body" className="col-span-full mt-[16px]">
            {kind === "email" ? (
              <RichTextEditor
                disabled={disabled}
                value={data.content?.body}
                onEditorChange={a => {
                  setData("content.body", a)
                }}
              />
            ) : (
              <CustomTextArea
                rows={8}
                error={
                  isPushNot &&
                  data?.content?.body?.length > pushNotificationLength
                    ? `Message above ${pushNotificationLength} characters may not be rendered completely to the end user (curr: ${
                        data?.content?.body?.length || 0
                      })`
                    : null
                }
                fill
                path="content.body"
              />
            )}
          </CustomFormGroup>

          {kind === "push" && (
            <>
              <FileUpload
                className="mt-[16px] w-max col-span-full"
                path="content.extraFields.image"
                label="Attachments"
              />
              <div className="col-span-full w-max ">
                <h6>Link To</h6>
                <div className="flex gap-4">
                  <StaticSelector
                    label="Type"
                    items={LINK_TYPES}
                    path="content.extraFields.data.action.value.type"
                  />

                  {data.content?.extraFields?.data?.action?.value?.type?.id ===
                    "sellable_item" && (
                    <SearchSelector
                      label="Item"
                      query={SEARCH_SELLABLE_ITEMS}
                      path="content.extraFields.data.action.value.id"
                    />
                  )}
                </div>
              </div>
            </>
          )}
          <Divider />
          <div>
            <h6 className="mb-2 flex items-center">
              <span className="mr-2">Selective Broadcast</span>
              <DynamicSelector path="userQuery" query={ALL_USER_QUERIES} />
            </h6>
            <div>
              <>
                {filtersState?.map((filter, idx) => {
                  const field = filter?.field

                  return (
                    <TemplateFilter
                      disabled={disabled}
                      className={"grid vt-form-grid grid-cols-4 gap-x-3 mb-3"}
                      basePath="filters"
                      index={idx}
                      key={field?.id}
                      filter={field}
                      allFilters={allFilters}
                      filters={filtersState}
                      onRemove={index => removeListItem("filters", index)}
                    >
                      {() => {
                        return (
                          <>
                            <RadioGroup
                              disabled={disabled}
                              className="flex items-start mt-4"
                              selectedValue={filter?.exclude}
                              onChange={e => {
                                const value = parseInt(e.currentTarget.value)
                                setData(`filters.${idx}.exclude`, value)
                              }}
                              inline
                            >
                              <Radio label="Send to" value={0} />
                              <Radio label="Do not Send to" value={1} />
                            </RadioGroup>
                          </>
                        )
                      }}
                    </TemplateFilter>
                  )
                })}

                <CustomButton
                  disabled={
                    disabled || filtersState?.length === allFilters?.length
                  }
                  icon="plus"
                  outlined
                  intent="success"
                  text="Field"
                  onClick={() => {
                    addListItem("filters", {
                      exclude: 0,
                      value: [],
                    })
                  }}
                />
              </>
            </div>
          </div>
        </CustomCard>
      </FormController>
    </SubHeader>
  )
}

export default CreateNotificationTrigger
